// Every SDK call in the app lives here. posthog-js loads lazily (its own
// chunk, after the first route has painted) from the `no-external` entry, so
// it never fetches a script from the host; the EU host, autocapture and
// hash-route pageviews are on, session recording is off. `track()` is the only
// way a learn event leaves the device: a typed allow-list of ids, counts and
// flags, never a note or free text, fire-and-forget so a hanging host cannot
// hold up a step, and a persisted queue that replays offline events with the
// time they happened.
import type { CaptureResult, PostHog, PostHogConfig } from "posthog-js";
import { readAsset, writeAsset } from "../assetCache";
import { POLICY } from "../dataCache";
import { getLocale } from "../i18n";
import type { MarkerBand } from "@proef/domain";
import { ACADEMY_PATH_FLAG_KEY } from "./contract";
import { setUnpublishedPayload } from "./flags";
import { isRecord } from "./guards";
import { getIdentity, subscribeIdentity } from "./identity";

const API_HOST = "https://eu.i.posthog.com";
const UI_HOST = "https://eu.posthog.com";
const QUEUE_KEY = "user:analytics:queue";
const QUEUE_LIMIT = 500;

type TasteFormat = "single" | "horizontal" | "vertical" | "diagonal";
type StepKind = "read" | "check" | "taste" | "drill";

interface Where {
  level_id?: number;
  module_id?: string;
  lesson_id?: string;
  /** Set inside a specialisation's lesson (ticket 219), from the manifest and never from the lesson id's text. */
  track_id?: string;
}

/** Why a marking fell back to pending: a fixed word, never the provider's own message. */
export type MarkingFailure =
  | "unavailable"
  | "rate_limited"
  | "credits"
  | "timeout"
  | "invalid"
  | "switched_off"
  | "no_key"
  | "no_route"
  | "out_of_budget";

export interface EventProps {
  path_viewed: Where;
  lesson_started: Where;
  check_answered: Where & { check_id: string; correct: boolean };
  lesson_completed: Where & {
    step_kinds: readonly StepKind[];
    with_note: boolean;
    taste_format: TasteFormat | "none";
  };
  drill_answered: Where & {
    drill_kind: "aroma" | "marker";
    item_id: string;
    correct: boolean;
    first_exposure: boolean;
    weeks_since_first_drill: number;
    due_count: number;
  };
  level_completed: { level_id: number };
  note_saved: { depth: "quick" | "deep"; blind: boolean; lesson_id?: string };
  /** From a comparative lesson step (its ids) or from a template of the library (its id): ids and counts only. */
  flight_created: {
    taste_format: Exclude<TasteFormat, "single">;
    wine_count: number;
  } & ({ lesson_id: string; step_id: number } | { template_id: string });
  /** A taste step put off ("no glass tonight"): the denominator of the outcome metric. */
  taste_deferred: { level_id: number; module_id: string; lesson_id: string; step_id: number };
  /** One opening of a list; `group` is the group's own list, which carries no group id. */
  shopping_list_viewed: {
    level_id: number;
    entry: "path" | "level" | "lesson" | "group";
    items_n: number;
    have_n: number;
  };
  /** A tick or untick; the ids name the line's first need and no wine, so a glass line has a null slot. */
  shopping_item_toggled: {
    level_id: number;
    lesson_id?: string;
    step_id?: number;
    slot: number | null;
    have: boolean;
  };
  shopping_list_exported: { level_id: number; method: "share" | "copy" | "print"; items_n: number };
  /** Once per reveal, in a solo note, a flight or a paper: ids and booleans, never the call's words. */
  blind_reveal: {
    context: "solo" | "flight" | "paper";
    family_actual: string;
    family_correct: boolean;
    grape_correct: boolean;
    /** `null` when no country was called. */
    country_correct: boolean | null;
    level: number;
    confusion_targeted: boolean;
  };
  paper_started: { paper_id: string; level: number; wines: number; time_budget_s: number };
  paper_submitted: { paper_id: string; elapsed_s: number; within_time: boolean };
  /** `score_band` is the lower end of a range; `sections_marked` counts sections with an answer. */
  paper_marked: { paper_id: string; score_band: string; sections_marked: number };
  calibration_viewed: { axis: string; abs_delta_steps: number; participants_n: number };
  /** Mastery (ticket 219): ids, bands, counts and fixed words only; never an essay, a verdict or a score. */
  track_unlocked_viewed: { tracks_shown: readonly string[] };
  track_enrolled: { track_id: string; level_at_enrol: number };
  track_mastery_viewed: { track_id: string; mastery_band: MarkerBand };
  research_prompt_started: { prompt_id: string; quarter: string };
  research_submitted: { prompt_id: string; days_elapsed: number; datasets_used: number };
  self_assessment_completed: { prompt_id: string; criteria_met: number };
  badge_awarded: { badge_id: string; track_id: string };
  marking_failed: { paper_id: string; reason: MarkingFailure };
  marking_appealed: {
    paper_id: string;
    criteria_count: number;
    moved: boolean;
    appeal_n: number;
  };
}

export type EventName = keyof EventProps;

const SHARED = [
  "level_id",
  "module_id",
  "lesson_id",
  "track_id",
  "locale",
  "offline",
  "app_version",
] as const;
const PLAIN = ["locale", "offline", "app_version"] as const;

/** The whole vocabulary: a property not listed for its event is dropped before send. */
export const ALLOWED_PROPERTIES: Readonly<Record<EventName, readonly string[]>> = {
  path_viewed: SHARED,
  lesson_started: SHARED,
  check_answered: [...SHARED, "check_id", "correct"],
  lesson_completed: [...SHARED, "step_kinds", "with_note", "taste_format"],
  drill_answered: [
    ...SHARED,
    "drill_kind",
    "item_id",
    "correct",
    "first_exposure",
    "weeks_since_first_drill",
    "due_count",
  ],
  level_completed: SHARED,
  note_saved: ["depth", "blind", "lesson_id", "locale", "offline", "app_version"],
  flight_created: [
    "lesson_id",
    "step_id",
    "template_id",
    "taste_format",
    "wine_count",
    "locale",
    "offline",
    "app_version",
  ],
  taste_deferred: [...SHARED, "step_id"],
  shopping_list_viewed: [
    "level_id",
    "entry",
    "items_n",
    "have_n",
    "locale",
    "offline",
    "app_version",
  ],
  shopping_item_toggled: [
    "level_id",
    "lesson_id",
    "step_id",
    "slot",
    "have",
    "locale",
    "offline",
    "app_version",
  ],
  shopping_list_exported: ["level_id", "method", "items_n", "locale", "offline", "app_version"],
  blind_reveal: [
    "context",
    "family_actual",
    "family_correct",
    "grape_correct",
    "country_correct",
    "level",
    "confusion_targeted",
    "locale",
    "offline",
    "app_version",
  ],
  paper_started: [
    "paper_id",
    "level",
    "wines",
    "time_budget_s",
    "locale",
    "offline",
    "app_version",
  ],
  paper_submitted: ["paper_id", "elapsed_s", "within_time", "locale", "offline", "app_version"],
  paper_marked: ["paper_id", "score_band", "sections_marked", "locale", "offline", "app_version"],
  calibration_viewed: [
    "axis",
    "abs_delta_steps",
    "participants_n",
    "locale",
    "offline",
    "app_version",
  ],
  track_unlocked_viewed: ["tracks_shown", ...PLAIN],
  track_enrolled: ["track_id", "level_at_enrol", ...PLAIN],
  track_mastery_viewed: ["track_id", "mastery_band", ...PLAIN],
  research_prompt_started: ["prompt_id", "quarter", ...PLAIN],
  research_submitted: ["prompt_id", "days_elapsed", "datasets_used", ...PLAIN],
  self_assessment_completed: ["prompt_id", "criteria_met", ...PLAIN],
  badge_awarded: ["badge_id", "track_id", ...PLAIN],
  marking_failed: ["paper_id", "reason", ...PLAIN],
  marking_appealed: ["paper_id", "criteria_count", "moved", "appeal_n", ...PLAIN],
};

/**
 * The brief's four insights and the events and properties each one reads, so a
 * change to the allow-list that would blind an insight fails a test.
 */
export const INSIGHT_INPUTS: readonly {
  readonly name: string;
  readonly inputs: Readonly<Record<string, readonly string[]>>;
}[] = [
  { name: "Lessons per WAU", inputs: { lesson_completed: ["level_id", "lesson_id"] } },
  {
    name: "Drill accuracy trend",
    inputs: { drill_answered: ["correct", "first_exposure", "weeks_since_first_drill"] },
  },
  {
    name: "Level 1 completion ≤ 30 d",
    inputs: { lesson_started: ["level_id"], level_completed: ["level_id"] },
  },
  {
    name: "Streak engagement",
    inputs: { lesson_completed: [], drill_answered: [], note_saved: [] },
  },
  {
    name: "Deferred glass gets poured",
    inputs: {
      taste_deferred: ["level_id", "module_id", "lesson_id", "step_id"],
      lesson_completed: ["lesson_id", "with_note"],
    },
  },
  {
    name: "List activation",
    inputs: {
      shopping_item_toggled: ["level_id", "have"],
      shopping_list_viewed: ["level_id"],
    },
  },
  {
    name: "Time to pour",
    inputs: {
      taste_deferred: ["lesson_id"],
      lesson_completed: ["lesson_id", "with_note"],
    },
  },
  {
    name: "Viewed a list before deferring",
    inputs: { shopping_list_viewed: [], taste_deferred: [] },
  },
  {
    name: "Blind accuracy by family",
    inputs: { blind_reveal: ["family_actual", "family_correct", "context"] },
  },
  {
    name: "Mock-paper completion",
    inputs: {
      paper_started: ["paper_id"],
      paper_submitted: ["paper_id", "within_time"],
      paper_marked: ["paper_id", "score_band"],
    },
  },
  { name: "Calibration trend", inputs: { calibration_viewed: ["axis", "abs_delta_steps"] } },
  {
    name: "Specialisation enrolment",
    inputs: {
      level_completed: ["level_id"],
      track_enrolled: ["track_id", "level_at_enrol"],
      lesson_completed: ["track_id"],
    },
  },
];

type Value = string | number | boolean | string[] | null;
type Properties = Record<string, Value>;

interface Captured {
  event: EventName;
  properties: Properties;
  /** When it happened, ISO; replays keep it. */
  timestamp: string;
}

let sdk: PostHog | undefined;
let loading: Promise<void> | undefined;
let enabled = false;
let buffer: Captured[] = [];
let queued: Captured[] | undefined;
let persistChain: Promise<void> = Promise.resolve();
let stopListening: (() => void) | undefined;

function readEnv(key: string): string | undefined {
  const value: unknown = import.meta.env[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

// ---- URL hygiene --------------------------------------------------------

/** Hash routes carry ids only, but a query inside a route (or the URL) could hold free text. */
export function scrubUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = "";
    const query = parsed.hash.indexOf("?");
    if (query >= 0) parsed.hash = parsed.hash.slice(0, query);
    // An invite token rides in the /i/ page's fragment and, until boot moves it, in #/invite/<token>.
    if (parsed.hash.startsWith("#/invite/")) parsed.hash = "#/invite";
    else if (parsed.pathname.endsWith("/i/")) parsed.hash = "";
    return parsed.href;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

/** A link property: web addresses lose query and invite tokens, and a mail or SMS share link (whose
 * body holds the invite) keeps only its scheme. */
function scrubLink(value: string): string {
  if (/^https?:\/\//i.test(value)) return scrubUrl(value);
  const scheme = /^(mailto|sms|tel):/i.exec(value);
  return scheme === null ? value : scheme[0];
}

function scrubRecord(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    out[key] = typeof value === "string" && /url|referrer/i.test(key) ? scrubLink(value) : value;
  }
  return out;
}

/** The SDK's `before_send`: query strings never leave the device, whichever property carries them. */
export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null;
  const next: CaptureResult = { ...event, properties: scrubRecord(event.properties) };
  if (event.$set !== undefined) next.$set = scrubRecord(event.$set);
  if (event.$set_once !== undefined) next.$set_once = scrubRecord(event.$set_once);
  return next;
}

// ---- the allow-list -----------------------------------------------------

const ID_VALUE = /^[A-Za-z0-9._:-]{1,80}$/;

/** Ids, counts and flags only: a string with a space in it is not an id, so free text cannot ride along. */
function cleanValue(value: unknown): Value | undefined {
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") return ID_VALUE.test(value) ? value : undefined;
  if (Array.isArray(value)) {
    const items = (value as unknown[]).filter(
      (item): item is string => typeof item === "string" && ID_VALUE.test(item),
    );
    return items.length === value.length ? items : undefined;
  }
  return undefined;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

function build(event: EventName, props: object): Captured {
  const allowed = ALLOWED_PROPERTIES[event];
  const properties: Properties = {};
  const given = new Map<string, unknown>(Object.entries(props));
  for (const key of allowed) {
    if (!given.has(key)) continue;
    const value = cleanValue(given.get(key));
    if (value !== undefined) properties[key] = value;
  }
  properties["locale"] = getLocale();
  properties["offline"] = isOffline();
  properties["app_version"] = __APP_VERSION__;
  return { event, properties, timestamp: new Date().toISOString() };
}

// ---- delivery -----------------------------------------------------------

function persistQueue(): void {
  const events = (queued ?? []).slice(-QUEUE_LIMIT);
  persistChain = persistChain
    .then(() => writeAsset(QUEUE_KEY, POLICY.user.version, { events }))
    .catch(() => undefined);
}

function isCaptured(value: unknown): value is Captured {
  if (!isRecord(value)) return false;
  const event = value["event"];
  return (
    typeof event === "string" &&
    typeof value["timestamp"] === "string" &&
    isRecord(value["properties"]) &&
    event in ALLOWED_PROPERTIES
  );
}

async function loadQueue(): Promise<Captured[]> {
  const stored = await readAsset(
    QUEUE_KEY,
    POLICY.user.version,
    (v): v is unknown => v !== undefined,
  );
  const events: unknown = isRecord(stored) ? stored["events"] : undefined;
  return Array.isArray(events) ? events.filter(isCaptured) : [];
}

function send(captured: Captured, replayed: boolean): void {
  try {
    sdk?.capture(
      captured.event,
      replayed ? { ...captured.properties, offline: true } : captured.properties,
      { timestamp: new Date(captured.timestamp) },
    );
  } catch {
    // The SDK is best effort; a throwing capture must never reach the learner.
  }
}

async function replayQueued(): Promise<void> {
  queued ??= await loadQueue();
  if (sdk === undefined || isOffline() || queued.length === 0) return;
  const events = queued;
  queued = [];
  persistQueue();
  for (const captured of events) send(captured, true);
}

function deliver(captured: Captured): void {
  // The SDK starts when the browser is idle, which a busy device can put after the first screens:
  // an event captured before `initAnalytics` runs waits in the buffer when a key is configured,
  // instead of being lost. Without a key nothing is ever captured.
  if (!enabled && readEnv("VITE_POSTHOG_KEY") === undefined) return;
  if (isOffline()) {
    queued ??= [];
    queued.push({ ...captured, properties: { ...captured.properties, offline: true } });
    persistQueue();
    return;
  }
  if (sdk === undefined) {
    buffer.push(captured);
    return;
  }
  send(captured, false);
}

/** The only way an event is captured. Returns at once; nothing here can fail a step. */
export function track<E extends EventName>(event: E, props: EventProps[E]): void {
  deliver(build(event, props));
}

// ---- the SDK ------------------------------------------------------------

function configFor(identity: string | undefined): Partial<PostHogConfig> {
  return {
    api_host: API_HOST,
    ui_host: UI_HOST,
    autocapture: true,
    // The plain "history_change" watches the path only; a hash router needs the hash too.
    capture_pageview: { path: true, hash: true },
    disable_capture_url_hashes: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_web_experiments: true,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    advanced_disable_toolbar_metrics: true,
    mask_all_element_attributes: true,
    mask_all_text: false,
    property_denylist: ["note", "freeText", "text"],
    persistence: "localStorage+cookie",
    secure_cookie: true,
    cross_subdomain_cookie: false,
    person_profiles: "identified_only",
    before_send: scrubEvent,
    ...(identity === undefined ? {} : { bootstrap: { distinctID: identity } }),
  };
}

function identify(): void {
  const identity = getIdentity();
  if (sdk === undefined || identity === undefined || sdk.get_distinct_id() === identity) return;
  sdk.identify(identity);
}

async function start(key: string): Promise<void> {
  const { default: posthog } = await import("posthog-js/no-external");
  posthog.init(key, configFor(getIdentity()));
  sdk = posthog;
  identify();
  const stopIdentity = subscribeIdentity(identify);
  const onOnline = (): void => {
    void replayQueued();
  };
  window.addEventListener("online", onOnline);
  stopListening = () => {
    stopIdentity();
    window.removeEventListener("online", onOnline);
  };
  posthog.onFeatureFlags(() => {
    setUnpublishedPayload(posthog.getFeatureFlagPayload(ACADEMY_PATH_FLAG_KEY));
  });
  for (const captured of buffer) send(captured, false);
  buffer = [];
  await replayQueued();
}

/**
 * Loads and starts the SDK; call it once the first route has painted. With no
 * `VITE_POSTHOG_KEY` nothing is loaded, the flag stays unknown and the app
 * shows today's screen: failing closed is the default, not a degraded mode.
 */
export function initAnalytics(options: { key?: string } = {}): void {
  if (loading !== undefined) return;
  const key = options.key ?? readEnv("VITE_POSTHOG_KEY");
  if (key === undefined || key === "") return;
  enabled = true;
  loading = start(key).catch(() => {
    // A blocked or failed load leaves the app exactly as it was without analytics.
  });
}

/** Resolves when the SDK has started and everything captured so far has been handed over or persisted. */
export async function flushAnalytics(): Promise<void> {
  await loading;
  await persistChain;
}

/** Back to a cold start, as a restart would be; what is persisted stays (tests). */
export function resetAnalytics(): void {
  stopListening?.();
  stopListening = undefined;
  sdk = undefined;
  loading = undefined;
  enabled = false;
  buffer = [];
  queued = undefined;
}
